import { isBlank } from './utils';

function formatMessage(message: any, path: any) {
    return `Parsing error on ${path}: ${message}`;
}

function schemaError(message: any, path: any): void {
    const mesg = formatMessage(message, path);
    throw new Error(mesg);
}

function schemaWarning(message: any, path: any): void {
    const mesg = formatMessage(message, path);
    throw new Error(mesg);
}

export class SchemaPreprocessor {
    static preprocess(jsonSchema: any, path = '/'): any {
        jsonSchema = jsonSchema || {};
        if (jsonSchema.type === 'object') {
            SchemaPreprocessor.checkProperties(jsonSchema, path);
            SchemaPreprocessor.checkAndCreateFieldsets(jsonSchema, path);
        } else if (jsonSchema.type === 'array') {
            SchemaPreprocessor.checkItems(jsonSchema, path);
        }
        SchemaPreprocessor.normalizeWidget(jsonSchema);
        SchemaPreprocessor.recursiveCheck(jsonSchema, path);
    }

    private static checkProperties(jsonSchema: any, path: string) {
        if (isBlank(jsonSchema.properties)) {
            jsonSchema.properties = {};
            schemaWarning('Provided json schema does not contain a \'properties\' entry. Output schema will be empty', path);
        }
    }

    private static checkAndCreateFieldsets(jsonSchema: any, path: string) {
        if (jsonSchema.fieldsets === undefined) {
            if (jsonSchema.order !== undefined) {
                SchemaPreprocessor.replaceOrderByFieldsets(jsonSchema);
            } else {
                SchemaPreprocessor.createFieldsets(jsonSchema);
            }
        }
        SchemaPreprocessor.checkFieldsUsage(jsonSchema, path);
    }

    private static checkFieldsUsage(jsonSchema: any, path: string) {
        const fieldsId: string[] = Object.keys(jsonSchema.properties);
        const usedFields: any = {};
        for (const fieldset of jsonSchema.fieldsets) {
            for (const fieldId of fieldset.fields) {
                if (usedFields[fieldId] === undefined) {
                    usedFields[fieldId] = [];
                }
                usedFields[fieldId].push(fieldset.id);
            }
        }

        for (const fieldId of fieldsId) {
            if (usedFields.hasOwnProperty(fieldId)) {
                if (usedFields[fieldId].length > 1) {
                    schemaError(`${fieldId} is referenced by more than one fieldset: ${usedFields[fieldId]}`, path);
                }
                delete usedFields[fieldId];
            } else if (jsonSchema.required.indexOf(fieldId) > -1) {
                schemaError(`${fieldId} is a required field but it is not referenced as part of a 'order' or a 'fieldset' property`, path);
            } else {
                delete jsonSchema[fieldId];
                schemaWarning(`Removing unreferenced field ${fieldId}`, path);
            }
        }

        for (const remainingfieldsId in usedFields) {
            if (usedFields.hasOwnProperty(remainingfieldsId)) {
                schemaWarning(`Referencing non-existent field ${remainingfieldsId} in one or more fieldsets`, path);
            }
        }
    }

    private static createFieldsets(jsonSchema: any) {
        jsonSchema.order = Object.keys(jsonSchema.properties);
        SchemaPreprocessor.replaceOrderByFieldsets(jsonSchema);
    }

    private static replaceOrderByFieldsets(jsonSchema: any) {
        jsonSchema.fieldsets = [
            {
                id: 'fieldset-default',
                title: jsonSchema.title || '',
                description: jsonSchema.description || '',
                name: jsonSchema.name || '',
                fields: jsonSchema.order
            }
        ];
        delete jsonSchema.order;
    }

    private static normalizeWidget(fieldSchema: any) {
        let widget = fieldSchema.widget;
        if (widget === undefined) {
            widget = { id: fieldSchema.type };
        } else if (typeof widget === 'string') {
            widget = { id: widget };
        }
        if (!widget.id) widget.id = fieldSchema.type;
        fieldSchema.widget = widget;
    }

    private static checkItems(jsonSchema: any, path: string) {
        if (jsonSchema.items === undefined) {
            schemaError('No \'items\' property in array', path);
        }
    }

    private static recursiveCheck(jsonSchema: any, path: string) {
        if (jsonSchema.type === 'object') {
            for (const fieldId in jsonSchema.properties) {
                if (jsonSchema.properties.hasOwnProperty(fieldId)) {
                    const fieldSchema = jsonSchema.properties[fieldId];
                    SchemaPreprocessor.preprocess(fieldSchema, path + fieldId + '/');
                }
            }
            if (jsonSchema.hasOwnProperty('definitions')) {
                for (const fieldId in jsonSchema.definitions) {
                    if (jsonSchema.definitions.hasOwnProperty(fieldId)) {
                        const fieldSchema = jsonSchema.definitions[fieldId];
                        SchemaPreprocessor.removeRecursiveRefProperties(fieldSchema, `#/definitions/${fieldId}`);
                        SchemaPreprocessor.preprocess(fieldSchema, path + fieldId + '/');
                    }
                }
            }
        } else if (jsonSchema.type === 'array') {
            SchemaPreprocessor.preprocess(jsonSchema.items, path + '*/');
        }
    }

    private static removeRecursiveRefProperties(jsonSchema: any, definitionPath: any) {
        // to avoid infinite loop
        if (jsonSchema.type === 'object') {
            for (const fieldId in jsonSchema.properties) {
                if (jsonSchema.properties.hasOwnProperty(fieldId)) {
                    if (jsonSchema.properties[fieldId].$ref && jsonSchema.properties[fieldId].$ref === definitionPath) {
                        delete jsonSchema.properties[fieldId];
                    } else if (jsonSchema.properties[fieldId].type === 'object') {
                        SchemaPreprocessor.removeRecursiveRefProperties(jsonSchema.properties[fieldId], definitionPath);
                    }
                }
            }
        }
    }
}
